import React, { useState } from "react";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth, db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { ref, get } from "firebase/database";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaLock, FaEnvelope, FaShieldAlt } from "react-icons/fa";

const AdminLogin = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Reusable function to check admin authorization
    const handlePostAuthentication = async (user) => {
        // Check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);
        if (usersSnapshot.exists()) {
            const usersData = usersSnapshot.val();
            const adminUser = Object.values(usersData).find(
                (u) => u.email === user.email
            );
            if (adminUser) {
                // Optionally, check for admin role: adminUser.role === 'admin'
                navigate("/admin");
                return;
            }
        }
        setError("You are not authorized as an admin.");
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");

        if (!validateEmail(email)) {
            setError("Please enter a valid email address.");
            setLoading(false);
            return;
        }

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            await handlePostAuthentication(userCredential.user);
        } catch (err) {
            switch (err.code) {
                case "auth/invalid-email":
                    setError("Invalid email address format.");
                    break;
                case "auth/user-disabled":
                    setError("Your account has been disabled. Please contact support.");
                    break;
                case "auth/user-not-found":
                    setError("No account found with this email address.");
                    break;
                case "auth/wrong-password":
                    setError("Incorrect password. Please try again.");
                    break;
                case "auth/too-many-requests":
                    setError("Too many failed attempts. Please try again later or reset your password.");
                    break;
                case "auth/network-request-failed":
                    setError("Network error. Please check your internet connection.");
                    break;
                default:
                    setError(`Login failed: ${err.message}`);
                    break;
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError("");

        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            await handlePostAuthentication(result.user);
        } catch (err) {
            // Don't show error if user just closed the popup
            if (err.code === "auth/popup-closed-by-user" || err.code === "auth/cancelled-popup-request") {
                setLoading(false);
                return;
            }

            switch (err.code) {
                case "auth/popup-blocked":
                    setError("Please allow popups for this site to sign in with Google.");
                    break;
                case "auth/account-exists-with-different-credential":
                    setError("This email is already registered. Please use your original sign-in method.");
                    break;
                case "auth/user-disabled":
                    setError("Your account has been disabled. Contact support.");
                    break;
                default:
                    setError("Unable to sign in with Google. Please try again.");
            }
            setLoading(false);
        }
    };

    return (
        <div className="font-space bg-gray-900 text-white min-h-screen">
            <Navbar />
            <section className="relative overflow-hidden py-12 sm:py-16 md:py-20">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 to-sky-900/30 -z-10"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col lg:flex-row items-center justify-center gap-12">
                    {/* Visual Element */}
                    <div className="hidden lg:flex lg:w-1/2 justify-center items-center">
                        <div className="relative w-full max-w-lg">
                            <div className="absolute -top-10 -left-10 w-32 h-32 bg-teal-600 rounded-full filter blur-3xl opacity-30"></div>
                            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-sky-600 rounded-full filter blur-3xl opacity-30"></div>
                            <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-12 shadow-2xl backdrop-blur-sm">
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-24 h-24 bg-gradient-to-r from-teal-500 to-sky-600 rounded-full flex items-center justify-center mb-6">
                                        <FaShieldAlt className="text-4xl text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold mb-3">Admin Login</h3>
                                    <p className="text-gray-400">
                                        Only authorized admins can access the admin dashboard.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Form Section */}
                    <div className="w-full lg:w-1/2 max-w-md">
                        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-2xl p-6 sm:p-8 shadow-2xl backdrop-blur-sm">
                            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-sky-300">
                                Admin Login
                            </h2>
                            {error && (
                                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-900/50 text-red-300 rounded-lg border border-red-700 text-xs sm:text-sm text-center">
                                    {error}
                                </div>
                            )}
                            <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Email</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FaEnvelope className="text-gray-500" />
                                        </div>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            placeholder="Enter your admin email"
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition text-white placeholder-gray-500"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-400 mb-2">Password</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <FaLock className="text-gray-500" />
                                        </div>
                                        <input
                                            type="password"
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            placeholder="Enter your password"
                                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition text-white placeholder-gray-500"
                                            required
                                        />
                                    </div>
                                </div>
                                <div>
                                    <button
                                        type="submit"
                                        className="w-full px-6 sm:px-8 py-3 sm:py-3.5 rounded-full bg-gradient-to-r from-teal-600 to-sky-600 hover:from-teal-500 hover:to-sky-500 text-white text-sm sm:text-base font-medium transition-all duration-300 shadow-lg hover:shadow-teal-500/30 touch-manipulation"
                                        disabled={loading}
                                    >
                                        {loading ? "Logging in..." : "Login as Admin"}
                                    </button>
                                </div>
                            </form>

                            {/* Divider */}
                            <div className="relative my-4 sm:my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-4 bg-gray-800 text-gray-500">OR</span>
                                </div>
                            </div>

                            {/* Google Sign-in Button */}
                            <button
                                type="button"
                                onClick={handleGoogleLogin}
                                disabled={loading}
                                className="w-full px-4 sm:px-6 py-3 sm:py-3.5 rounded-full bg-gray-800 border border-gray-700 hover:bg-gray-700 text-white text-sm sm:text-base font-semibold transition-all duration-200 shadow-lg hover:shadow-xl active:scale-[0.98] flex justify-center items-center gap-2 sm:gap-3 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                            >
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                </svg>
                                <span className="truncate">{loading ? "Signing in..." : "Continue with Google"}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default AdminLogin; 